import React, { useEffect, useState, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PaperPlaneRight, ChatCircle } from "@phosphor-icons/react";

export function ChatList() {
  const { user } = useAuth();
  const [convs, setConvs] = useState([]);
  useEffect(() => { api.get("/conversations").then((r) => setConvs(r.data)); }, []);
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <div className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Inbox</div>
        <h1 className="font-display text-4xl mb-8">Conversations</h1>
        {convs.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-12 text-center">
            <ChatCircle size={32} className="mx-auto text-slate-400 mb-2" />
            <p className="text-sm text-slate-600">No conversations yet.</p>
          </div>
        ) : (
          <div className="border border-slate-200">
            {convs.map((c) => {
              const otherId = c.participants.find((p) => p !== user?.user_id);
              return (
                <Link key={c.conversation_id} to={`/chat/${c.conversation_id}`} className="block p-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50" data-testid={`conv-${c.conversation_id}`}>
                  <div className="font-semibold">{c.participant_names?.[otherId] || "Conversation"}</div>
                  <div className="text-sm text-slate-600 truncate">{c.last_message || "—"}</div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export function ChatRoom() {
  const { id } = useParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/conversations/${id}/messages`);
    setMessages(data);
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [id]);

  useEffect(() => {
    scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight);
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/conversations/${id}/messages`, { text });
      setText("");
      load();
    } catch (err) {
      console.error("Failed to send message:", err);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      <div className="pt-24 pb-6 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 flex-1 w-full flex flex-col">
        <Link to="/chat" className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">← Inbox</Link>
        <div ref={scrollRef} className="flex-1 border border-slate-200 p-4 overflow-y-auto space-y-3 min-h-[50vh] max-h-[60vh]">
          {messages.map((m) => {
            const mine = m.sender_id === user?.user_id;
            return (
              <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[75%] px-3 py-2 ${mine ? "bg-brand text-white" : "bg-slate-100 text-slate-900"}`} data-testid={`msg-${m.message_id}`}>
                  <div className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">{m.sender_name}</div>
                  <div className="text-sm whitespace-pre-line">{m.text}</div>
                </div>
              </div>
            );
          })}
          {messages.length === 0 && <div className="text-center text-slate-400 text-sm py-12">No messages yet. Say hello.</div>}
        </div>
        <form onSubmit={send} className="mt-4 flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" className="flex-1 border border-slate-300 px-4 py-3" data-testid="chat-input" />
          <button type="submit" className="bg-brand text-white px-5 font-semibold flex items-center gap-2 hover:bg-[#122148]" data-testid="chat-send-btn">
            <PaperPlaneRight size={18} weight="fill" /> Send
          </button>
        </form>
      </div>
      <Footer />
    </div>
  );
}
